import { HighlightsRiskLevel, TrendDirection } from "@asd/customer-portal-api";
import { formatDate, ReportKey, toWallClockDate } from "@asd/customer-portal-ui";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import ReportIcon from "@mui/icons-material/Report";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Stack, Tooltip } from "@mui/material";
import type { GridColDef, GridColumnGroupHeaderParams, GridRenderCellParams } from "@mui/x-data-grid/models";
import moment, { Moment } from "moment-timezone";

import FuzzyHighlight from "~/components/shared/FuzzyHighlight";
import { env } from "~/utils/env";
import { USDollar } from "~/utils/stringConverters";
import { getUnitValueLabel } from "~/utils/unitUsageHelpers";
import { MonthOption } from "./MonthsAutocomplete";
import SnapshotRenderCell from "./SnapshotRenderCell";

type ExportableColDef = GridColDef & { colorInExport?: boolean };

export const getTrendDisplay = (value: TrendDirection | undefined | null) => {
  let trendDisplay: { color: string | undefined; icon: string | null } = { color: undefined, icon: null };
  if (value === "Decreasing") {
    trendDisplay = { color: "success.main" as const, icon: "trendingDown" as const };
  } else if (value === "Unchanged") {
    trendDisplay = { color: "default" as const, icon: "trendingFlat" as const };
  } else if (value === "Increasing") {
    trendDisplay = { color: "error.main" as const, icon: "trendingUp" as const };
  }

  return trendDisplay;
};

export const getRiskDisplay = (value: HighlightsRiskLevel | undefined | null) => {
  let riskLevel: { color: string | undefined; icon: string | null } = { color: undefined, icon: null };
  if (value === "Depleted") {
    riskLevel = { color: "error.main" as const, icon: "report" as const };
  } else if (value === "Critical") {
    riskLevel = { color: "warning.main" as const, icon: "warning" as const };
  } else if (value === "High") {
    riskLevel = { color: "custom.main" as const, icon: "error" as const };
  }

  return riskLevel;
};

export const getVarianceColor = (value: number | undefined | null) => {
  let varianceColor: string | undefined = undefined;
  if (value != null && value < -10) {
    varianceColor = "success.main" as const;
  } else if (value != null && value > 10) {
    varianceColor = "error.main" as const;
  }
  return varianceColor;
};

export const getDivisionVarianceColor = (value: number | undefined | null) => {
  let varianceColor: string | undefined = undefined;
  if (value != null && value <= 90) {
    varianceColor = "success.main" as const;
  } else if (value != null && value >= 110) {
    varianceColor = "error.main" as const;
  }
  return varianceColor;
};

const PROGRAM_COLUMNS: GridColDef[] = [
  { field: "program", headerName: "Program", flex: 1, valueGetter: (_value, row) => row.authorizationManagement?.programName },
  {
    field: "programAdmin",
    headerName: "Program Admin",
    flex: 1,
    valueGetter: (_value, row) => row.authorizationManagement?.programAdminName,
    renderCell: (params: GridRenderCellParams<any, string>) => {
      const value: string = params.value as string;
      const rootProps = (params.api as any).rootProps || {};
      const currentFilters = rootProps.tableFilters || {};
      const searchTerm = currentFilters["name"] || "";
      return <FuzzyHighlight search={searchTerm} text={String(value ?? "")} />;
    },
  },
  {
    field: "careManager",
    headerName: "Care Manager",
    flex: 1,
    valueGetter: (_value, row) => row.authorizationManagement?.caseManagerName,
    renderCell: (params: GridRenderCellParams<any, string>) => {
      const value: string = params.value as string;
      const rootProps = (params.api as any).rootProps || {};
      const currentFilters = rootProps.tableFilters || {};
      const searchTerm = currentFilters["name"] || "";
      return <FuzzyHighlight search={searchTerm} text={String(value ?? "")} />;
    },
  },
];

const SUMMARY_COLUMNS: GridColDef[] = [
  ...PROGRAM_COLUMNS,
  {
    field: "member",
    headerName: "Participant/Member",
    flex: 1,
    valueGetter: (_value, row) => `${row.firstName} ${row.lastName}`,
    renderCell: (params: GridRenderCellParams<any, string>) => {
      const value: string = params.value as string;
      const rootProps = (params.api as any).rootProps || {};
      const currentFilters = rootProps.tableFilters || {};
      const searchTerm = currentFilters["name"] || "";
      return <FuzzyHighlight search={searchTerm} text={String(value ?? "")} />;
    },
  },
  { field: "description", headerName: "Service", flex: 1 },
  {
    field: "startDate",
    headerName: "Start Date",
    minWidth: 100,
    flex: 1,
    type: "date",
    valueGetter: toWallClockDate,
    valueFormatter: formatDate,
  },
  {
    field: "stopDate",
    headerName: "End Date",
    minWidth: 100,
    flex: 1,
    type: "date",
    valueGetter: toWallClockDate,
    valueFormatter: formatDate,
  },
  {
    field: "reportTypes",
    headerName: "Issues",
    flex: 1,
    valueGetter: (reportTypes: string[]) => reportTypes?.join(", "),
    renderHeader: (params) => (
      <Tooltip title="The reason(s) the service is at risk" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "monthsFlagged",
    headerName: "Months",
    flex: 1,
    headerAlign: "left",
    type: "number",
    renderHeader: (params) => (
      <Tooltip title="The number of months the service has showed in Aggregate Reports" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "allocatedUnits",
    headerName: "Total Approved Units",
    flex: 1,
    type: "number",
    renderCell: (params: GridRenderCellParams<any, number>) => getUnitValueLabel(params.value, params.row.unitLabel),
  },
  {
    field: "claimUnits",
    headerName: "Actual Usage",
    flex: 1,
    type: "number",
    renderCell: (params: GridRenderCellParams<any, number>) => getUnitValueLabel(params.value, params.row.unitLabel),
  },
  {
    field: "variancePercentage",
    headerName: "Variance",
    flex: 1,
    type: "number",
    valueGetter: (value) => value ?? 0,
    renderCell: (params: GridRenderCellParams<any, number>) => {
      return <Box>{params.value?.toFixed(2)}%</Box>;
    },
    renderHeader: (params) => (
      <Tooltip title="% difference between actual usage and expected usage" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "trendDirection",
    headerName: "Trend",
    flex: 1,
    renderCell: (params: GridRenderCellParams<any, string>) => {
      const { color, icon } = getTrendDisplay(params.value as TrendDirection);
      return (
        <Box sx={{ color, gap: 1, display: "flex", alignItems: "center" }}>
          {icon === "trendingDown" && <TrendingDownIcon />}
          {icon === "trendingFlat" && <TrendingFlatIcon />}
          {icon === "trendingUp" && <TrendingUpIcon />}
          {params.value}
        </Box>
      );
    },
    renderHeader: (params) => (
      <Tooltip title="Whether variance has increased or decreased since last month" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "riskLevel",
    headerName: "Risk Level",
    flex: 1,
    renderCell: (params: GridRenderCellParams<any, string>) => {
      const { color, icon } = getRiskDisplay(params.value as HighlightsRiskLevel);
      return (
        <Box sx={{ color, gap: 1, display: "flex", alignItems: "center" }}>
          {icon === "report" && <ReportIcon />}
          {icon === "warning" && <WarningIcon />}
          {icon === "error" && <ErrorIcon />}
          {params.value}
        </Box>
      );
    },
    renderHeader: (params) => (
      <Tooltip
        title="Overall priority calculated from Issues, Months, Variance, Trend, and Exhaustion Rate. A higher risk level indicates greater potential concern"
        arrow
      >
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
];

const OVERUSAGE_COLUMNS: ExportableColDef[] = [
  ...PROGRAM_COLUMNS,
  { field: "member", headerName: "Participant/Member", flex: 1, valueGetter: (_value, row) => `${row.firstName} ${row.lastName}` },
  { field: "description", headerName: "Service", flex: 1 },
  {
    field: "startDate",
    headerName: "Start Date",
    minWidth: 100,
    flex: 1,
    type: "date",
    valueGetter: toWallClockDate,
    valueFormatter: formatDate,
  },
  {
    field: "stopDate",
    headerName: "End Date",
    minWidth: 100,
    flex: 1,
    type: "date",
    valueGetter: toWallClockDate,
    valueFormatter: formatDate,
  },
  {
    field: "snapshot",
    headerName: "Snapshot",
    flex: 2,
    minWidth: 300,
    headerAlign: "left",
    type: "number",
    valueGetter: (_value, row) => (row.claimUnits / row.allocatedUnits) * 100,
    renderCell: (params: GridRenderCellParams<any, number>) => <SnapshotRenderCell {...params} />,
  },
  {
    field: "allocatedUnits",
    headerName: "Total Approved Units",
    flex: 1,
    type: "number",
    renderCell: (params: GridRenderCellParams<any, number>) => getUnitValueLabel(params.value, params.row.unitLabel),
  },
  {
    field: "claimUnits",
    headerName: "Actual Usage",
    flex: 1,
    type: "number",
    renderCell: (params: GridRenderCellParams<any, number>) => getUnitValueLabel(params.value, params.row.unitLabel),
  },
  {
    field: "expectedUnits",
    headerName: "Expected Usage",
    flex: 1,
    type: "number",
    renderCell: (params: GridRenderCellParams<any, number>) => getUnitValueLabel(Number(params.value?.toFixed(2)), params.row.unitLabel),
    renderHeader: (params) => (
      <Tooltip title="The target unit usage to date" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "variancePercentage",
    headerName: "Variance",
    flex: 1,
    type: "number",
    colorInExport: true,
    valueGetter: (value) => value ?? 0,
    renderCell: (params: GridRenderCellParams<any, number>) => {
      const color = getVarianceColor(params.value);
      return <Box sx={{ color }}>{params.value?.toFixed(2)}%</Box>;
    },
    renderHeader: (params) => (
      <Tooltip title="% difference between actual usage and expected usage" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
];

const UNUSED_COLUMNS: GridColDef[] = OVERUSAGE_COLUMNS.reduce((acc, col) => {
  if (col.field !== "variancePercentage") {
    acc.push({
      ...col,
      ...(col.field === "claimUnits" && { headerName: "Total Actual Usage" }),
    });
  }
  return acc;
}, [] as GridColDef[]);

const DEPLETED_COLUMNS: GridColDef[] = OVERUSAGE_COLUMNS.reduce((acc, col) => {
  if (!["snapshot", "expectedUnits"].includes(col.field)) {
    const { renderCell, ...column } = col;

    acc.push({
      ...column,
      ...(col.field !== "variancePercentage" && { renderCell }),
      ...(col.field === "variancePercentage" && {
        field: "exceededUnits",
        headerName: "Units Over",
        renderHeader: (params) => (
          <Tooltip title="Number of units used over the total approved unit limit" arrow>
            <Stack alignItems="center" direction="row">
              <span>{params.colDef.headerName}</span>
              <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
            </Stack>
          </Tooltip>
        ),
      }),
    });
  }
  return acc;
}, [] as GridColDef[]);

const OVERTIME_COLUMNS: GridColDef[] = OVERUSAGE_COLUMNS.reduce((acc, col) => {
  if (!["snapshot", "expectedUnits", "variancePercentage"].includes(col.field)) {
    acc.push({
      ...col,
      ...(col.field === "claimUnits" && { headerName: "Total Actual Usage" }),
    });
  }
  return acc;
}, [] as GridColDef[]).concat([
  {
    field: "claimUnitsOverReportPeriod",
    headerName: "Actual Usage This Month",
    flex: 1,
    type: "number",
    renderCell: (params: GridRenderCellParams<any, number>) => getUnitValueLabel(params.value, params.row.unitLabel),
  },
]);

const dollarRenderer = (params: GridRenderCellParams<any, number>) => {
  return USDollar.format(params.value ?? 0);
};

export const DIVISION_COLUMNS: GridColDef[] = [
  { field: "programName", headerName: "Program", flex: 1 },
  {
    field: "memberId",
    headerName: "Member/Case ID",
    flex: 1,
  },
  {
    field: "lastName",
    headerName: "Last Name",
    flex: 1,
  },
  {
    field: "firstName",
    headerName: "First Name",
    flex: 1,
  },
  {
    field: "monthBudget",
    headerName: "Billing Budget",
    type: "number",
    flex: 1,
    renderCell: dollarRenderer,
    renderHeader: (params) => (
      <Tooltip title="Sum of units*standard bill rate for each service code" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "monthExpense",
    headerName: "Expense",
    type: "number",
    flex: 1,
    renderCell: dollarRenderer,
    renderHeader: (params) => (
      <Tooltip title="Sum of check payments on behalf of member/participant" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "monthAee",
    headerName: "Additional Employer Expenses",
    type: "number",
    flex: 1,
    renderCell: dollarRenderer,
    renderHeader: (params) => (
      <Tooltip title="Sum of purchases made by the employer" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "monthVariance",
    headerName: "Variance",
    type: "number",
    flex: 1,
    valueGetter: (value) => value ?? 0,
    renderCell: (params: GridRenderCellParams<any, number>) => {
      return <Box>{params.value?.toFixed(2)}</Box>;
    },
    renderHeader: (params) => (
      <Tooltip title="Budgeted dollars - all expenses" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "monthPercentBudget",
    headerName: "% of Budget",
    type: "number",
    flex: 1,
    valueGetter: (value) => value ?? 0,
    renderCell: (params: GridRenderCellParams<any, number>) => {
      const color = getDivisionVarianceColor(params.value);
      return <Box sx={{ color }}>{params.value?.toFixed(2)}%</Box>;
    },
    renderHeader: (params) => (
      <Tooltip title="% of budget spent" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "fyBudget",
    headerName: "Billing Budget",
    type: "number",
    flex: 1,
    renderCell: dollarRenderer,
    renderHeader: (params) => (
      <Tooltip title="Sum of units*standard bill rate for each service code" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "fyExpense",
    headerName: "Expense",
    type: "number",
    flex: 1,
    renderCell: dollarRenderer,
    renderHeader: (params) => (
      <Tooltip title="Sum of check payments on behalf of member/participant" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "fyAee",
    headerName: "Additional Employer Expenses",
    type: "number",
    flex: 1,
    renderCell: dollarRenderer,
    renderHeader: (params) => (
      <Tooltip title="Sum of purchases made by the employer" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "fyVariance",
    headerName: "Variance",
    type: "number",
    flex: 1,
    valueGetter: (value) => value ?? 0,
    renderCell: (params: GridRenderCellParams<any, number>) => {
      return <Box>{params.value?.toFixed(2)}</Box>;
    },
    renderHeader: (params) => (
      <Tooltip title="Budgeted dollars - all expenses" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
  {
    field: "fyPercentBudget",
    headerName: "% of Budget",
    type: "number",
    flex: 1,
    valueGetter: (value) => value ?? 0,
    renderCell: (params: GridRenderCellParams<any, number>) => {
      const color = getDivisionVarianceColor(params.value);
      return <Box sx={{ color }}>{params.value?.toFixed(2)}%</Box>;
    },
    renderHeader: (params) => (
      <Tooltip title="% of budget spent" arrow>
        <Stack alignItems="center" direction="row">
          <span>{params.colDef.headerName}</span>
          <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
        </Stack>
      </Tooltip>
    ),
  },
];

const COLUMNS_MAP: Record<ReportKey, GridColDef[]> = {
  summary: SUMMARY_COLUMNS,
  overUsage: OVERUSAGE_COLUMNS,
  unused: UNUSED_COLUMNS,
  depleted: DEPLETED_COLUMNS,
  overtime: OVERTIME_COLUMNS,
  division: DIVISION_COLUMNS,
};

const COLUMN_GROUPING_MAP: Record<ReportKey, any> = {
  summary: null,
  overUsage: null,
  unused: null,
  depleted: null,
  overtime: null,
  division: [
    {
      groupId: "Member/Participant",
      children: [{ field: "programName" }, { field: "memberId" }, { field: "lastName" }, { field: "firstName" }],
      headerClassName: "centerColumnGroupHeader",
    },
    {
      groupId: "Monthly",
      children: [
        { field: "monthBudget" },
        { field: "monthExpense" },
        { field: "monthAee" },
        { field: "monthVariance" },
        { field: "monthPercentBudget" },
      ],
      headerClassName: "centerColumnGroupHeader",
      renderHeaderGroup: (params: GridColumnGroupHeaderParams) => (
        <Tooltip title="Summary of active services in the current month" arrow>
          <Stack alignItems="center" direction="row">
            <span>{params.headerName}</span>
            <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
          </Stack>
        </Tooltip>
      ),
    },
    {
      groupId: "Plan Year-To-Date",
      children: [{ field: "fyBudget" }, { field: "fyExpense" }, { field: "fyAee" }, { field: "fyVariance" }, { field: "fyPercentBudget" }],
      headerClassName: "centerColumnGroupHeader",
      renderHeaderGroup: (params: GridColumnGroupHeaderParams) => (
        <Tooltip title="Summary of active services in the current fiscal year (10/1 - current month)" arrow>
          <Stack alignItems="center" direction="row">
            <span>{params.headerName}</span>
            <InfoIcon color="primary" fontSize="small" sx={{ ml: 0.5 }} />
          </Stack>
        </Tooltip>
      ),
    },
  ],
};

export const getColumns = (selectedReport: ReportKey, supportCoordinator: { enabled: boolean; title: string }): GridColDef[] => {
  return COLUMNS_MAP[selectedReport].reduce((acc, column) => {
    if (!supportCoordinator.enabled && ["programAdmin", "careManager"].includes(column.field)) {
      return acc;
    }

    if (column.field === "careManager") {
      acc.push({ ...column, headerName: supportCoordinator.title });
    } else {
      acc.push(column);
    }

    return acc;
  }, [] as GridColDef[]);
};

export const getColumnGroupingModel = (selectedReport: ReportKey) => {
  return COLUMN_GROUPING_MAP[selectedReport];
};

const MONTH_VALUE_FORMAT = "YYYY-MM";
const STANDARD_MONTH_COUNT = 12;

const buildMonthOption = (month: Moment): MonthOption => ({
  label: month.format("MMMM YYYY"),
  value: month.format(MONTH_VALUE_FORMAT),
  lastDay: month.daysInMonth(),
});

export const buildMonthOptionFromValue = (monthValue: string): MonthOption => ({
  ...buildMonthOption(moment(monthValue, MONTH_VALUE_FORMAT)),
  value: monthValue,
});

// Optional "YYYY-MM-DD" from the environment; when set, no month before the cutoff's month is offered
const AUTHORIZATION_START_DATE_CUTOFF_MONTH = ((): string | null => {
  const cutoff = env.REACT_APP_AUTHORIZATION_START_DATE_CUTOFF;
  if (!cutoff) {
    return null;
  }

  const cutoffDate = moment(cutoff, "YYYY-MM-DD", true);
  if (!cutoffDate.isValid()) {
    // eslint-disable-next-line no-console
    console.warn(`Ignoring invalid REACT_APP_AUTHORIZATION_START_DATE_CUTOFF "${cutoff}", expected YYYY-MM-DD`);
    return null;
  }

  return cutoffDate.format(MONTH_VALUE_FORMAT);
})();

// Optional day of the month (1-28) on which the prior month's report is released; defaults to the 1st
const REPORT_RELEASE_DAY_OF_MONTH = ((): number => {
  const rawDay = env.REACT_APP_REPORT_RELEASE_DAY_OF_MONTH;
  if (!rawDay) {
    return 1;
  }

  const day = Number(rawDay);
  if (!Number.isInteger(day) || day < 1 || day > 28) {
    // eslint-disable-next-line no-console
    console.warn(`Ignoring invalid REACT_APP_REPORT_RELEASE_DAY_OF_MONTH "${rawDay}", expected a whole number from 1 to 28`);
    return 1;
  }

  return day;
})();

// The previous 12 months, most recent first, but never earlier than the authorization start date cutoff month (if set).
// "Today" is based on the org's default timezone, not the browser's. The newest month is not offered until the
// release day of the month (at midnight org time), e.g. with a release day of 5, October appears at 12:00am on Nov 5th
export const getStandardMonthOptions = (): MonthOption[] =>
  Array.from({ length: STANDARD_MONTH_COUNT })
    .map((_, index) =>
      buildMonthOption(
        moment()
          .subtract(REPORT_RELEASE_DAY_OF_MONTH - 1, "day")
          .startOf("month")
          .subtract(index + 1, "month"),
      ),
    )
    .filter(({ value }) => !AUTHORIZATION_START_DATE_CUTOFF_MONTH || value >= AUTHORIZATION_START_DATE_CUTOFF_MONTH);

export const getColumnValue = (columns: GridColDef[], columnName: string, row: any) => {
  const col = columns.find((c: any) => c.field === columnName);
  if (!col) return row[columnName];

  const rawValue = col.valueGetter
    ? (col.valueGetter as (value: any, row: any, ...args: any[]) => any)(row[columnName], row)
    : row[columnName];

  if (col.valueFormatter) {
    const formatter = col.valueFormatter as (value: any, row: any, ...args: any[]) => any;
    return formatter(rawValue, row);
  }

  return rawValue;
};
